import React, { Fragment, useCallback, useMemo } from "react"
import { ISections } from "types/ISections"
import SectionV2 from "components/Sections/SectionV2"
import Skeleton from "components/Skeleton/Skeleton"
import useWindowSize from "hooks/useWindowSize"
import { IFinalEvent } from "types/event"
import DateFilterationSkeleton from "components/Skeleton/EventsDateFilterationComponentSkeleton"
import { useLang } from "hooks/useLang"
import ArtistsSection from "../ArtistsSection/artistsSection"
import VenuesSection from "../VenuesSection/venusesSection"

interface EventsSectionsProps {
    sectionsData: ISections[]
    videos_sections?: ISections[]
    filteredEvents: IFinalEvent[]
    isLoading: boolean
}

const EventsSections: React.FC<EventsSectionsProps> = ({
    sectionsData,
    filteredEvents,
    isLoading,
}) => {
    const { isMobile } = useWindowSize()
    const { multiLangVars } = useLang()

    // Speeds up section event lookup vs filtering for each section
    const filteredEventsById = useMemo(() => {
        const map = new Map<string, IFinalEvent>()
        for (const ev of filteredEvents) map.set(ev._id, ev)
        return map
    }, [filteredEvents])

    const renderBrandingSections = useCallback(
        (section: ISections) => {
            switch (section.type) {
                case "artists":
                    return (
                        <div key={section._id}>
                            <ArtistsSection
                                data={section.artists ?? []}
                                isLoading={isLoading}
                            />
                        </div>
                    )

                case "venues":
                    return (
                        <div key={section._id}>
                            <VenuesSection
                                data={section.venues ?? []}
                                isLoading={isLoading}
                            />
                        </div>
                    )

                case "organizations":
                    return (
                        <div key={section._id}>
                            <ArtistsSection
                                showTitle={false}
                                isLoading={false}
                                data={section?.events}
                            />
                        </div>
                    )

                default:
                    return null
            }
        },
        [isLoading]
    )

    if (isLoading) {
        return (
            <>
                <div className="mt-24 layout">
                    <DateFilterationSkeleton />
                </div>

                <div className="layout flex flex-col gap-4 mt-4">
                    <Skeleton
                        className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"
                        title
                        type="event_card"
                    />
                    <Skeleton title type="event_card" />
                </div>
            </>
        )
    }

    return (
        <div>
            {sectionsData?.map((section) => {
                // 1) render custom sections (artists/venues)
                const branding = renderBrandingSections(section)
                if (branding) return branding

                // 2) render generic sections
                const sectionEvents = (section.events ?? [])
                    .map((se) => filteredEventsById.get(se._id))
                    .filter(Boolean) as IFinalEvent[]

                const hasVideos = (section.videos?.length ?? 0) > 0
                if (sectionEvents.length === 0 && !hasVideos) return null

                return (
                    <Fragment key={section._id}>
                        <SectionV2
                            {...section}
                            id={section._id}
                            events={section.events}
                            videos={section.videos}
                            title={
                                section?.[
                                    multiLangVars("title") as
                                        | "en_title"
                                        | "ar_title"
                                ]
                            }
                            type={section.type}
                            maxEventsInARow={section.max_row_events}
                            layout={
                                isMobile
                                    ? section.mobile_layout
                                    : section.layout
                            }
                            aspectRatio={section.aspect_ratio}
                        />
                    </Fragment>
                )
            })}
        </div>
    )
}

export default EventsSections
